/**
 * Main entry point for CompuTein multi-simulation frontend
 */

import { SimulationManager } from './core/SimulationManager';
import { Grid } from './ui/Grid';
import { Controls } from './ui/Controls';
import type { CellPosition, SimulationState } from './types/simulation';
import { positionFromCellId } from './types/simulation';

class App {
  private manager: SimulationManager;
  private grid: Grid;
  private controls: Controls;
  private fpsUpdateInterval: number = 0;

  constructor() {
    // Create simulation manager
    this.manager = new SimulationManager(3, 4, {
      onActiveChange: (cellId) => this.handleActiveChange(cellId),
      onSimulationStateChange: (cellId, state) => this.handleStateChange(cellId, state),
      onError: (cellId, error) => this.handleError(cellId, error),
    });

    // Create grid
    const gridElement = document.getElementById('grid') as HTMLDivElement;
    this.grid = new Grid(gridElement, 3, 4, {
      onCellClick: (pos) => this.handleCellClick(pos),
      onCellInitialize: (pos, canvas) => this.handleCellInitialize(pos, canvas),
    });

    // Create controls
    this.controls = new Controls({
      onPlayAll: () => this.manager.playAll(),
      onPauseAll: () => this.manager.pauseAll(),
      onPlayActive: () => this.manager.playActive(),
      onPauseActive: () => this.manager.pauseActive(),
      onLoadScenario: (scenario) => this.handleLoadScenario(scenario),
    });

    // Handle window resize
    window.addEventListener('resize', this.handleResize);

    // Start FPS update interval
    this.startFpsUpdates();

    console.log('CompuTein multi-simulation manager initialized');
  }

  private handleCellClick(position: CellPosition): void {
    this.manager.setActive(position);
    this.grid.setActive(position);
  }

  private async handleCellInitialize(
    position: CellPosition,
    canvas: HTMLCanvasElement
  ): Promise<void> {
    try {
      await this.manager.initializeCell(position, canvas);
      this.grid.markCellInitialized(position);

      // Auto-select first initialized cell
      if (!this.manager.activeId) {
        this.manager.setActive(position);
        this.grid.setActive(position);
      }
    } catch (error) {
      console.error(`Failed to initialize cell at ${position.row},${position.col}:`, error);
    }
  }

  private handleActiveChange(cellId: string | null): void {
    this.controls.setActiveCell(cellId);
    this.grid.setActiveById(cellId);
  }

  private handleStateChange(cellId: string, state: SimulationState): void {
    this.grid.updateCellState(cellId, state);
  }

  private handleError(cellId: string, error: Error): void {
    console.error(`Error in cell ${cellId}:`, error);
  }

  private async handleLoadScenario(scenario: import('./types/scenario').Scenario): Promise<void> {
    try {
      await this.manager.loadScenarioToActive(scenario);
    } catch (error) {
      console.error('Failed to load scenario:', error);
    }
  }

  private handleResize = (): void => {
    // Resize all initialized simulations
    this.manager.resizeAllCells((pos) => {
      const cell = this.grid.getCell(pos);
      return cell?.getSize() ?? { width: 0, height: 0 };
    });
  };

  private startFpsUpdates(): void {
    this.fpsUpdateInterval = window.setInterval(() => {
      // Get average FPS across running simulations
      const states = this.manager.getAllStates();
      let totalFps = 0;
      let runningCount = 0;

      for (const state of states.values()) {
        if (state.status === 'running') {
          totalFps += state.fps;
          runningCount++;
        }
        // Update cell display
        this.grid.updateCellState(state.id, state);
      }

      const avgFps = runningCount > 0 ? Math.round(totalFps / runningCount) : 0;
      this.controls.setGlobalFps(avgFps);
    }, 500);
  }

  dispose(): void {
    window.removeEventListener('resize', this.handleResize);
    clearInterval(this.fpsUpdateInterval);
    this.manager.disposeAll();
    this.grid.dispose();
  }
}

// Initialize app when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  const app = new App();

  // Expose for debugging
  (window as unknown as { app: App }).app = app;
});
